import { RouteObject, Navigate } from 'react-router-dom'
import { getRouteComponent, getRoutePatternKey } from './routeRegistry'
import { AuthGuard } from '@/components/auth/AuthGuard'

interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

/**
 * Generate React Router routes from user_routes (database navigation)
 */
export function generateRoutesFromUserRoutes(
  userRoutes: UserRoute[],
  publicRoutes: RouteObject[] = []
): RouteObject[] {
  const routes: RouteObject[] = [
    // Public routes (always available)
    ...publicRoutes,
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
  ]

  /**
   * Recursively process routes and their children
   */
  function processRoute(route: UserRoute, parentPath: string = '') {
    let fullPath: string
    
    if (route.path.startsWith('/')) {
      // Absolute path - check if it exists in route registry
      // If not, try joining with parentPath (for cases like /users under /user-management)
      const absolutePath = route.path
      const ComponentForAbsolute = getRouteComponent(absolutePath)
      
      if (ComponentForAbsolute) {
        // Absolute path exists in registry (e.g., /shipping-lines/create)
        fullPath = absolutePath
      } else if (parentPath) {
        // Absolute path doesn't exist, try joining with parent
        // (e.g., /users under /user-management becomes /user-management/users)
        const joinedPath = `${parentPath}${absolutePath}`
        const ComponentForJoined = getRouteComponent(joinedPath)
        if (ComponentForJoined) {
          fullPath = joinedPath
        } else {
          // Neither absolute nor joined path exists, use absolute as fallback
          fullPath = absolutePath
        }
      } else {
        // No parent path, use absolute as-is
        fullPath = absolutePath
      }
    } else {
      // Relative path - join with parentPath
      fullPath = parentPath ? `${parentPath}${route.path}` : route.path
    }

    // Get component for this route; register using registry pattern key for dynamic routes
    const Component = getRouteComponent(fullPath)
    const routePath = getRoutePatternKey(fullPath) ?? fullPath
    const alreadyRegistered = routes.some((r) => r.path === routePath)

    if (Component && !alreadyRegistered) {
      routes.push({
        path: routePath,
        element: (
          <AuthGuard>
            <Component />
          </AuthGuard>
        ),
      })
    }

    // Process children
    // Use fullPath as the parent path for children
    // fullPath is always set (either absolute, joined, or relative path)
    // This ensures children are processed with the correct parent context
    // For example: /user-management (parent) -> /users (child) becomes /user-management/users
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => {
        // Pass fullPath as parent so children can be joined correctly
        // The processRoute function will:
        // 1. Check if child's absolute path exists in registry (e.g., /shipping-lines/create)
        // 2. If not, try joining with parentPath (e.g., /users + /user-management = /user-management/users)
        processRoute(child, fullPath)
      })
    }
  }

  function ensureRoute(path: string) {
    const routePath = getRoutePatternKey(path) ?? path
    const hasRoute = routes.some((r) => r.path === routePath)
    if (hasRoute) return
    const Component = getRouteComponent(path)
    if (!Component) return
    routes.push({
      path: routePath,
      element: (
        <AuthGuard>
          <Component />
        </AuthGuard>
      ),
    })
  }

  // Process all user routes
  userRoutes.forEach((route) => {
    processRoute(route)
  })

  // Always ensure dashboard route exists (even if user_routes is empty or doesn't include it)
  // Check if dashboard route already exists
  const hasDashboard = routes.some((r) => r.path === '/dashboard')
  if (!hasDashboard) {
    const DashboardComponent = getRouteComponent('/dashboard')
    if (DashboardComponent) {
      routes.push({
        path: '/dashboard',
        element: (
          <AuthGuard>
            <DashboardComponent />
          </AuthGuard>
        ),
      })
    }
  }

  // Always ensure user activity route exists (it's accessed via link, not from user_routes)
  // Add it BEFORE the catch-all route so it can be matched
  ensureRoute('/user-management/user-activity/:id')

  // Store details (linked from Stores table "View Details", not in sidebar navigation)
  ensureRoute('/organization/stores/:id/details')
  ensureRoute('/store-management/stores/:id/details')

  // Client Asset Catalog workspace (opened from Clients → Manage Asset Catalog)
  ensureRoute('/organization/clients/:clientId/asset-catalog')
  ensureRoute('/organization/clients/:clientId/asset-catalog/overview')
  ensureRoute('/organization/clients/:clientId/asset-catalog/assets')
  ensureRoute('/organization/clients/:clientId/asset-catalog/assets/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/assets/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/categories')
  ensureRoute('/organization/clients/:clientId/asset-catalog/categories/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/categories/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/functions')
  ensureRoute('/organization/clients/:clientId/asset-catalog/functions/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/functions/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brands')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brands/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brands/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brand-models')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brand-models/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/brand-models/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/tags')
  ensureRoute('/organization/clients/:clientId/asset-catalog/tags/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/tags/:id')
  ensureRoute('/organization/clients/:clientId/asset-catalog/spec/:specType')
  ensureRoute('/organization/clients/:clientId/asset-catalog/spec/:specType/create')
  ensureRoute('/organization/clients/:clientId/asset-catalog/spec/:specType/:id')

  // Legacy bookmarks
  ensureRoute('/asset-configuration')
  ensureRoute('/asset-configuration/overview')
  ensureRoute('/asset-configuration/assets')
  ensureRoute('/asset-configuration/assets/create')
  ensureRoute('/asset-configuration/assets/:id')
  ensureRoute('/asset-configuration/categories')
  ensureRoute('/asset-configuration/functions')
  ensureRoute('/asset-configuration/brands')
  ensureRoute('/asset-configuration/brand-models')
  ensureRoute('/asset-configuration/tags')
  ensureRoute('/asset-configuration/spec/:specType')

  // Help pages (pinned in sidebar; always available regardless of user_routes)
  ensureRoute('/user-manual')
  ensureRoute('/faq')

  // Fallback to dashboard for any unmatched authenticated route
  // Only add catch-all if we have routes (don't redirect during initialization)
  if (routes.length > publicRoutes.length + 1) { // More than just public routes + root redirect
    routes.push({
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    })
  }

  return routes
}

